// Role hierarchy levels (higher = more access)
export const ROLE_HIERARCHY = {
  user: 1,
  project_manager: 2,
  senior_project_manager: 3,
  program_manager: 4,
  executive: 5,
  pmo: 6,
  admin: 10,
} as const;

export type AppRole = keyof typeof ROLE_HIERARCHY;

// Feature permissions mapping with primary (⭐) and secondary (◽) value per role
// Primary = core value proposition, Secondary = oversight/limited access
export const FEATURE_PERMISSIONS: Record<string, AppRole[]> = {
  // ========== USER (Pilot/Evaluator) ==========
  // ⭐ Primary: Meeting Hub, Dashboard (pilot-scoped), Feedback Widget
  // ◽ Secondary: Reports (draft only)
  // 🚫 Hidden: Admin/AI Controls
  
  // ========== PROJECT MANAGER ==========
  // ⭐ Primary: Task Board, Smart Inbox, Calendar, Meeting Hub
  // ◽ Secondary: Dashboard, Documents, Reports (view), Notifications
  // 🚫 Hidden: Stakeholder Map, Admin/AI, Branding
  
  // ========== SENIOR PROJECT MANAGER = PROJECT MANAGER ==========
  
  // ========== PROGRAM MANAGER ==========
  // ⭐ Primary: Dashboard, Reports, Risk Prediction, Stakeholder Map
  // ◽ Secondary: Meeting Hub, Tasks (oversight), Inbox
  // 🚫 Hidden: Admin/AI, Branding
  
  // ========== EXECUTIVE (Trust Consumer) ==========
  // ⭐ Primary: Executive Dashboard, Approved Reports, Decision Log, Strategy View
  // 🚫 Hidden: Meeting Hub, Tasks/Inbox (Zero operational detail)
  
  // ========== PMO ==========
  // ⭐ Primary: Executive Dashboard, Reports, Meeting Hub, Strategy, Stakeholder Mgmt, Audit
  // ◽ Secondary: Task Board (oversight)
  // 🚫 Hidden: Smart Inbox
  
  // ========== ADMIN ==========
  // Full access to all features
  
  // Dashboard - Primary for: User (pilot-scoped), Program Manager, Executive, PMO | Secondary for: PM
  dashboard: ['user', 'project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  
  // Meeting Hub - Primary for: User, PM, PMO | Secondary for: Program Manager | Hidden for: Executive
  meetings: ['user', 'project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'admin'],
  
  // Action Hub - replaces the old Task Board nav entry
  actionHub: ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'executive', 'admin'],
  
  // Smart Inbox - Primary for: PM | Secondary for: Program Manager | Hidden for: PMO, User, Executive
  inbox: ['project_manager', 'senior_project_manager', 'program_manager', 'admin'],
  
  // Calendar - Primary for: PM | Hidden for: Executive
  calendar: ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'admin'],
  
  // Documents - Secondary for: PM | Hidden for: Executive
  documents: ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'admin'],

  // Document Intelligence - review workbench for AI-detected project/portfolio/
  // strategy/budget candidates from synced documents; same role list as Action Hub.
  documentIntelligence: ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'executive', 'admin'],
  
  // Projects - Hidden for: Executive
  projects: ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'admin'],

  // Team - Hidden for: Executive
  team: ['project_manager', 'senior_project_manager', 'program_manager', 'pmo', 'admin'],
  
  // Reports - Primary for: Program Manager, Executive (approved only), PMO | Secondary for: PM (view), User (draft only)
  reports: ['user', 'project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  
  // Weekly Digest - Hidden for: Executive
  weeklyDigest: ['program_manager', 'pmo', 'admin'],
  
  // Strategy View - Primary for: Executive, PMO
  strategy: ['executive', 'pmo', 'admin'],
  
  // Stakeholder Management - Primary for: Program Manager, PMO | Hidden for: PM, Executive
  stakeholders: ['program_manager', 'pmo', 'admin'],
  
  // Activity/Audit - Primary for: Executive, PMO
  activity: ['executive', 'pmo', 'admin'],

  // Knowledge Base - PMO and Admin only
  knowledge: ['pmo', 'admin'],
  
  // Branding - PMO and Admin only | Hidden for: PM, Program Manager, Executive
  branding: ['pmo', 'admin'],
  
  // Feedback Widget - Primary for: User
  feedback: ['user', 'project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],

  // AI Controls - PMO and Admin for governance, Executive for transparency
  aiControls: ['executive', 'pmo', 'admin'],

  // Governance nav group — Signals, Risks, Dependencies, Actions Required,
  // Decisions, Audit Trail. Role set is the union of every role that
  // previously had access to any of these screens, so consolidation
  // doesn't take access away from anyone.
  signals: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  risks: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  dependencies: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  actionsRequired: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  decisions: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],
  auditTrail: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],

  // HITL Review Workbench - dedicated approval/policy/kill-switch surface,
  // separate from the Governance analysis areas above.
  hitlWorkbench: ['project_manager', 'senior_project_manager', 'program_manager', 'executive', 'pmo', 'admin'],

  // Tenant Settings - Admin only
  tenantSettings: ['admin'],

  // Integrations — connecting external systems (Jira, Azure DevOps, etc.)
  // and their credentials is an admin-level action, matching
  // Purpose of Jira integration.md §4/§8's RBAC + governance requirement.
  integrations: ['admin'],
  
  // Admin Dashboard - Admin only | Hidden for: User, PM, Program Manager, Executive
  adminDashboard: ['admin'],

  // Portfolio View - Program Manager and above
  portfolio: ['program_manager', 'executive', 'pmo', 'admin'],

  // Compliance Center - Admin only
  compliance: ['admin'],

  // Analytics - reuses the existing tenant-admin analytics page; admin only,
  // matching that page's own self-guard (useAdminRole).
  analytics: ['admin'],
};

// Check if a role has access to a feature
export function hasFeatureAccess(userRole: AppRole | null, feature: string): boolean {
  if (!userRole) return false;
  // Admin gets every module on their own tenant - a freshly-provisioned
  // org admin should see the full app, not just the handful of features
  // FEATURE_PERMISSIONS happens to list them under.
  if (userRole === 'admin') return true;
  const allowedRoles = FEATURE_PERMISSIONS[feature];
  if (!allowedRoles) return false;
  return allowedRoles.includes(userRole);
}

// Check if a role has minimum level access
export function hasMinimumRole(userRole: AppRole | null, minimumRole: AppRole): boolean {
  if (!userRole) return false;
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minimumRole];
}

// Get role display name
export function getRoleDisplayName(role: AppRole): string {
  const displayNames: Record<AppRole, string> = {
    user: 'User',
    project_manager: 'Project Manager',
    senior_project_manager: 'Senior Project Manager',
    program_manager: 'Program Manager',
    executive: 'Executive',
    pmo: 'PMO',
    admin: 'Administrator',
  };
  return displayNames[role] || role;
}
